/**
 * Telegram's HTML subset in a mailing text, read the way the users service checks
 * it: which tags and attributes may be there, that tags are closed, that `< > &`
 * are written as entities. Gives the tree the preview draws and, in plain words,
 * what is wrong — the server checks again on save; this is to see it while typing.
 */
/** The tag names Telegram takes, to what they mean. A `span` is only a spoiler. */
const TAGS = {
    b: 'b',
    strong: 'b',
    i: 'i',
    em: 'i',
    u: 'u',
    ins: 'u',
    s: 's',
    strike: 's',
    del: 's',
    'tg-spoiler': 'spoiler',
    span: 'spoiler',
    code: 'code',
    a: 'a',
    pre: 'pre',
    blockquote: 'blockquote',
};
/** HTML elements that never close: flagged, but nothing waits for their end. */
const VOID = new Set([
    'br',
    'hr',
    'img',
    'input',
    'meta',
    'link',
    'wbr',
    'source',
    'area',
    'base',
    'col',
    'embed',
    'param',
    'track',
]);
const SCHEME = /^(https?|tg|mailto):/i;
const LANGUAGE = /^language-(\S+)$/;
const NAMED = { lt: '<', gt: '>', amp: '&', quot: '"' };
const OPEN = /<([a-zA-Z][\w-]*)((?:\s+[^\s=>/]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?)*)\s*(\/?)>/y;
const CLOSE = /<\/([a-zA-Z][\w-]*)\s*>/y;
const ATTR = /([^\s=>/]+)(?:\s*=\s*("[^"]*"|'[^']*'|[^\s>]+))?/g;
const ENTITY = /&(#\d+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/y;
const ENTITIES = /&(#\d+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g;
/** The character an entity stands for, or null when Telegram doesn't take it. */
function decodeEntity(name) {
    if (!name.startsWith('#'))
        return NAMED[name] ?? null;
    const hex = name[1] === 'x' || name[1] === 'X';
    const code = parseInt(name.slice(hex ? 2 : 1), hex ? 16 : 10);
    return Number.isFinite(code) && code >= 0 && code <= 0x10ffff ? String.fromCodePoint(code) : null;
}
const attrValue = (raw) => raw.replace(/^["']|["']$/g, '').replace(ENTITIES, (m, name) => decodeEntity(name) ?? m);
export function parseTelegramHtml(html) {
    const nodes = [];
    const problems = [];
    const seen = new Set();
    const report = (p) => {
        const key = JSON.stringify(p);
        if (seen.has(key))
            return;
        seen.add(key);
        problems.push(p);
    };
    const stack = [{ name: '', children: nodes }];
    const top = () => stack[stack.length - 1];
    let text = '';
    const flush = () => {
        if (text)
            top().children.push({ type: 'text', text });
        text = '';
    };
    const open = (name, rawAttrs, selfClosing) => {
        const tag = TAGS[name];
        const attrs = [...rawAttrs.matchAll(ATTR)].map(([, attr = '', value]) => ({
            attr: attr.toLowerCase(),
            value: value == null ? null : attrValue(value),
        }));
        const spoilerSpan = attrs.some((a) => a.attr === 'class' && a.value === 'tg-spoiler');
        if (!tag || selfClosing || (name === 'span' && !spoilerSpan)) {
            report({ kind: 'tag', tag: name });
            // Its text still shows; its closing tag closes it without another complaint.
            if (!selfClosing && !VOID.has(name))
                stack.push({ name, children: top().children });
            return;
        }
        const el = { type: 'el', tag, children: [] };
        for (const { attr, value } of attrs) {
            if (tag === 'a' && attr === 'href' && value != null && SCHEME.test(value.trim())) {
                el.href = value.trim();
            }
            else if (tag === 'code' && attr === 'class' && value != null && LANGUAGE.test(value)) {
                el.language = LANGUAGE.exec(value)?.[1];
            }
            else if (tag === 'blockquote' && attr === 'expandable') {
                el.expandable = true;
            }
            else if (!(name === 'span' && attr === 'class' && value === 'tg-spoiler')) {
                report({ kind: 'attribute', tag: name, attr });
            }
        }
        top().children.push(el);
        stack.push({ name, children: el.children });
    };
    const close = (name) => {
        let at = stack.length - 1;
        while (at > 0 && stack[at].name !== name)
            at -= 1;
        if (at === 0) {
            report({ kind: 'stray_close', tag: name });
            return;
        }
        for (let k = stack.length - 1; k > at; k -= 1)
            report({ kind: 'unclosed', tag: stack[k].name });
        stack.length = at;
    };
    let i = 0;
    while (i < html.length) {
        const ch = html[i];
        if (ch === '<') {
            if (html.startsWith('<!--', i)) {
                report({ kind: 'comment' });
                const end = html.indexOf('-->', i + 4);
                i = end < 0 ? html.length : end + 3;
                continue;
            }
            if (html[i + 1] === '!' || html[i + 1] === '?') {
                report({ kind: html[i + 1] === '!' ? 'declaration' : 'processing_instruction' });
                const end = html.indexOf('>', i);
                i = end < 0 ? html.length : end + 1;
                continue;
            }
            CLOSE.lastIndex = i;
            const closing = CLOSE.exec(html);
            if (closing) {
                flush();
                close(closing[1].toLowerCase());
                i = CLOSE.lastIndex;
                continue;
            }
            OPEN.lastIndex = i;
            const opening = OPEN.exec(html);
            if (opening) {
                flush();
                open(opening[1].toLowerCase(), opening[2] ?? '', opening[3] === '/');
                i = OPEN.lastIndex;
                continue;
            }
            report({ kind: 'unescaped_lt' });
        }
        else if (ch === '>') {
            report({ kind: 'unescaped_gt' });
        }
        else if (ch === '&') {
            ENTITY.lastIndex = i;
            const entity = ENTITY.exec(html);
            if (entity) {
                const decoded = decodeEntity(entity[1]);
                if (decoded == null)
                    report({ kind: 'entity', name: entity[1] });
                text += decoded ?? entity[0];
                i = ENTITY.lastIndex;
                continue;
            }
            report({ kind: 'unescaped_amp' });
        }
        text += ch;
        i += 1;
    }
    flush();
    for (let k = stack.length - 1; k > 0; k -= 1)
        report({ kind: 'unclosed', tag: stack[k].name });
    return { nodes, problems };
}
/** What a reader sees: tags dropped, entities read. Its `.length` is what Telegram counts. */
export function visibleText(html) {
    const out = [];
    const walk = (nodes) => {
        for (const n of nodes) {
            if (n.type === 'text')
                out.push(n.text);
            else
                walk(n.children);
        }
    };
    walk(parseTelegramHtml(html).nodes);
    return out.join('');
}
export function problemText(p) {
    switch (p.kind) {
        case 'tag':
            return `Тег <${p.tag}> Telegram не поддерживает`;
        case 'attribute':
            return p.tag === 'a' && p.attr === 'href'
                ? 'Ссылка — только https://, http://, tg:// или mailto:'
                : `У <${p.tag}> не бывает атрибута ${p.attr}`;
        case 'unclosed':
            return `Не закрыт тег <${p.tag}>`;
        case 'stray_close':
            return `Лишний закрывающий тег </${p.tag}>`;
        case 'entity':
            return `&${p.name}; нельзя — только &lt; &gt; &amp; &quot; и числовые`;
        case 'unescaped_lt':
            return 'Знак < пишется как &lt;';
        case 'unescaped_gt':
            return 'Знак > пишется как &gt;';
        case 'unescaped_amp':
            return 'Знак & пишется как &amp;';
        case 'comment':
            return 'Комментарии <!-- --> нельзя';
        case 'declaration':
            return 'Конструкции <!…> нельзя';
        case 'processing_instruction':
            return 'Конструкции <?…?> нельзя';
    }
}
const PLAIN = [
    'unescaped_lt',
    'unescaped_gt',
    'unescaped_amp',
    'comment',
    'declaration',
    'processing_instruction',
];
/** A problem code from the server's `invalid_html` in the same words. */
export function serverProblemText(code) {
    if (code.startsWith('entity:'))
        return problemText({ kind: 'entity', name: code.slice(7) });
    if (code.startsWith('attribute:')) {
        const [tag = '', attr = ''] = code.slice(10).split('.');
        return problemText({ kind: 'attribute', tag, attr });
    }
    const kind = PLAIN.find((k) => k === code);
    return kind ? problemText({ kind }) : code;
}
